// Route: /library/folders — список папок Библиотеки.
// URL state: ?q, ?sort. Вид (карточки/список) — в localStorage (useViewMode),
// не в URL: запоминается между визитами, как в конспектах и записях.
//
// ЗАЧЕМ ОТДЕЛЬНАЯ СТРАНИЦА, если справа в Библиотеке уже есть рельс папок.
// Рельс — это ФИЛЬТР: клик по папке сужает список материалов и никуда не ведёт.
// Страница папки (с генерацией конспекта / карточек / теста по всей папке) до
// 2026-08-14 была недостижима из Библиотеки вовсе. Эта страница — вход в неё:
// клик по карточке открывает папку, а не фильтрует.
//
// ИСТОРИЯ, чтобы её снова не «почистили». Отдельный список коллекций уже
// существовал под `/collections` и был удалён при переходе на обучающую
// систему (LEARNING-SYSTEM-UI §2.1, «Отдельного списка коллекций больше нет»)
// — рельс считался его заменой. Практика показала, что рельс закрыл только
// фильтрацию, а вход в папку потерялся. Возврат — осознанное решение владельца
// (2026-08-14), вместе с подпунктами «Папки» и «Избранное» под Библиотекой.
//
// Раздел собран на каноне списков (rules/frontend-ui §27): SectionHeader +
// ListToolbar + EntityItem — тот же ритм, что у конспектов, тестов и записей.
// Ручную сетку ссылок сюда не возвращать: она разъезжается с остальными
// разделами при каждой правке канона.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconArrowsSort, IconFolder, IconFolderPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	type LibraryFolder,
	useLibraryFolders,
	useLibraryInvalidate,
} from "@/components/library/_api";
import { FolderCard } from "@/components/library/folder-card";
import { FolderDialog } from "@/components/library/folder-dialog";
import {
	CreateEmptyTile,
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { FilterDropdown, filterDropdownTrigger } from "@/components/shared/filter-pill";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useConfirm } from "@/hooks/use-confirm";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useViewMode } from "@/hooks/use-view-mode";
import { useLocale } from "@/i18n/use-locale";
import { api } from "@/lib/trpc-client";

/** Лёгкий веер наклонов плиток grid — «живая» картотека (как в конспектах). */
const TILTS = [-1.2, 0.8, -0.6, 1, -0.9, 0.6] as const;

/** `default` — порядок сервера (sortOrder, затем свежие сверху). */
const SORT_VALUES = ["default", "name", "materials"] as const;
type SortValue = (typeof SORT_VALUES)[number];

const searchSchema = z.object({
	q: z.string().optional(),
	sort: z.enum(SORT_VALUES).optional(),
});

export const Route = createFileRoute("/library/folders/")({
	validateSearch: tolerantSearch(searchSchema),
	component: FoldersPage,
	pendingComponent: PagePendingFallback,
});

function FoldersPage(): React.ReactElement {
	const { t } = useTranslation("library");
	const { t: tc } = useTranslation("common");
	const { locale } = useLocale();
	const search = useSearch({ from: "/library/folders/" });
	const navigate = useNavigate({ from: "/library/folders/" });
	const [view, setView] = useViewMode("library-folders");
	const [dialogOpen, setDialogOpen] = useState(false);
	/** `null` — создание, объект — переименование конкретной папки. */
	const [editing, setEditing] = useState<LibraryFolder | null>(null);

	const folders = useLibraryFolders();
	const invalidate = useLibraryInvalidate();
	const confirm = useConfirm();
	const online = useOnlineStatus();

	const deleteFolder = api.library.folders.delete.useMutation({
		onSuccess: () => {
			invalidate();
			toast.success(t("folders.deleted"));
		},
		onError: () => toast.error(t("folders.deleteError")),
	});

	const q = search.q ?? "";
	const sort: SortValue = search.sort ?? "default";

	function updateSearch(next: { q?: string; sort?: SortValue }): void {
		void navigate({
			search: (prev) => ({
				...prev,
				...("q" in next ? { q: next.q || undefined } : {}),
				...("sort" in next ? { sort: next.sort === "default" ? undefined : next.sort } : {}),
			}),
			replace: true,
		});
	}

	const items = useMemo(() => {
		const needle = q.trim().toLowerCase();
		const filtered = needle
			? folders.items.filter((folder) => folder.name.toLowerCase().includes(needle))
			: folders.items;
		if (sort === "name") {
			return filtered.toSorted((a, b) => a.name.localeCompare(b.name, locale));
		}
		if (sort === "materials") {
			return filtered.toSorted((a, b) => b.materialsCount - a.materialsCount);
		}
		return filtered;
	}, [folders.items, q, sort, locale]);

	function openCreate(): void {
		setEditing(null);
		setDialogOpen(true);
	}

	function openRename(folder: LibraryFolder): void {
		setEditing(folder);
		setDialogOpen(true);
	}

	function requestDelete(folder: LibraryFolder): void {
		// Удаление папки — серверная мутация (не PowerSync): офлайн она молча
		// не долетит, поэтому честно говорим об этом до подтверждения.
		if (!online) {
			toast.error(t("folders.deleteError"));
			return;
		}
		void (async () => {
			const ok = await confirm({
				title: t("folders.deleteConfirmTitle"),
				description: t("folders.deleteConfirmDescription", { name: folder.name }),
				confirmLabel: tc("actions.delete"),
				tone: "destructive",
			});
			if (ok) deleteFolder.mutate({ id: folder.id });
		})();
	}

	const sortOptions = SORT_VALUES.map((value) => ({
		value,
		label: t(`folders.sort.${value}`),
	}));
	const sortTrigger = filterDropdownTrigger(sortOptions, sort, t("folders.sort.label"), "default");
	const sortFilter = (stacked: boolean): React.ReactElement => (
		<FilterDropdown
			stacked={stacked}
			ariaLabel={t("folders.sort.label")}
			icon={<IconArrowsSort size={stacked ? 15 : 14} aria-hidden />}
			triggerLabel={stacked ? t("folders.sort.label") : sortTrigger.label}
			active={sortTrigger.active}
			value={sort}
			options={sortOptions}
			onChange={(next) => updateSearch({ sort: next as SortValue })}
		/>
	);

	const containerClass = view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK;
	const hasQuery = q.trim().length > 0;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconFolder />}
				title={t("folders.title")}
				description={t("folders.pageDescription")}
				actions={
					<Button size="default" onClick={openCreate} className="cursor-pointer">
						<IconFolderPlus size={16} />
						<span className="ml-1.5 hidden sm:inline">{t("folders.createTitle")}</span>
					</Button>
				}
			/>

			<ListToolbar
				search={{
					value: q,
					onChange: (next) => updateSearch({ q: next }),
					placeholder: t("folders.searchPlaceholder"),
				}}
				filters={sortFilter(false)}
				filtersSheet={sortFilter(true)}
				filtersActive={sortTrigger.active}
				view={view}
				onViewChange={setView}
			/>

			{folders.isLoading ? (
				<ListSkeleton view={view} />
			) : folders.isError ? (
				<ErrorState
					title={t("folders.loadError")}
					action={
						<Button variant="outline" onClick={folders.refetch} className="cursor-pointer">
							{tc("actions.retry")}
						</Button>
					}
				/>
			) : items.length === 0 ? (
				hasQuery ? (
					<EmptyState
						icon={<IconFolder />}
						title={t("folders.emptyFiltered")}
						action={
							<Button
								variant="outline"
								onClick={() => updateSearch({ q: "" })}
								className="cursor-pointer"
							>
								{t("filters.reset")}
							</Button>
						}
					/>
				) : (
					<EmptyState
						icon={<IconFolder />}
						title={t("folders.emptyTitle")}
						description={t("folders.emptyHint")}
						action={
							<Button onClick={openCreate} className="cursor-pointer">
								<IconFolderPlus size={16} />
								{t("folders.createTitle")}
							</Button>
						}
					/>
				)
			) : (
				<div className={containerClass}>
					{items.map((folder, i) => (
						<FolderCard
							key={folder.id}
							folder={folder}
							view={view}
							onRename={openRename}
							onDelete={requestDelete}
							tilt={view === "grid" ? TILTS[i % TILTS.length] : undefined}
						/>
					))}
					{/* Тактильная плитка «Новая папка» — только в grid (как в /notes):
						    в длинном списке кнопка шапки уезжает вверх, плитка остаётся. */}
					{view === "grid" && (
						<CreateEmptyTile
							category="doc"
							icon={<IconFolderPlus />}
							label={t("folders.createTitle")}
							onClick={openCreate}
						/>
					)}
				</div>
			)}

			<FolderDialog open={dialogOpen} onOpenChange={setDialogOpen} folder={editing} />
		</div>
	);
}

function ListSkeleton({ view }: { view: "grid" | "list" }): React.ReactElement {
	if (view === "grid") {
		return (
			<div className={LIST_PAGE_GRID} aria-busy="true">
				{Array.from({ length: 6 }, (_, i) => (
					<Skeleton key={`fg-${i}`} className="h-44 rounded-[var(--radius-card)]" />
				))}
			</div>
		);
	}
	return (
		<div className={LIST_PAGE_STACK} aria-busy="true">
			{Array.from({ length: 6 }, (_, i) => (
				<Skeleton key={`fl-${i}`} className="h-[72px] w-full rounded-[var(--radius-card)]" />
			))}
		</div>
	);
}
